<template>
  <v-dialog :value="show" max-width="1240" @input="(v) => !v && close()">
    <v-card v-if="order && form" class="amap">
      <div class="amap__head">
        <div>
          <div class="amap__title">Lieferadresse</div>
          <div class="amap__sub">{{ order.orderName }}</div>
        </div>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <div class="amap__body">
        <!-- Links: Adresse direkt bearbeiten (gleiche Felder + Speicherlogik wie EditOrderAddressDialog) -->
        <div class="amap__form">
          <div class="amap__grid">
            <v-text-field v-model="form.firstName" outlined dense hide-details label="Vorname" />
            <v-text-field v-model="form.lastName" outlined dense hide-details label="Nachname" />
            <v-text-field v-model="form.addressPrimary" outlined dense hide-details label="Straße + Hausnummer" class="amap__full" />
            <v-text-field v-model="form.addressSecondary" outlined dense hide-details label="Adresszusatz" class="amap__full" />
            <v-text-field v-model="form.company" outlined dense hide-details label="Firma" class="amap__full" />
            <v-text-field v-model="form.postCode" outlined dense hide-details label="PLZ" />
            <v-text-field v-model="form.city" outlined dense hide-details label="Ort" />
            <v-text-field v-model="form.province" outlined dense hide-details label="Region" />
            <v-text-field v-model="form.countryCode" outlined dense hide-details label="Land" placeholder="DE/FR/ES/US" />
            <v-text-field v-model="form.phone" outlined dense hide-details label="Telefon (für Zusteller)" class="amap__full" />
          </div>

          <div v-if="hints.length" class="amap__hints">
            <div v-for="h in hints" :key="h" class="amap__hint"><v-icon x-small color="#b26a00">mdi-alert-outline</v-icon> {{ h }}</div>
          </div>

          <div class="amap__row">
            <v-btn depressed rounded small :color="liveChecked ? '#2e7d32' : undefined" :dark="liveChecked" @click="$emit('toggle-checked', order)">
              <v-icon small left>{{ liveChecked ? "mdi-eye-check" : "mdi-eye-outline" }}</v-icon>{{ liveChecked ? "Geprüft" : "Als geprüft markieren" }}
            </v-btn>
            <v-btn text rounded small @click="copy"><v-icon small left>mdi-content-copy</v-icon>Kopieren</v-btn>
          </div>
          <div class="amap__links">
            <a :href="googleUrl" target="_blank" rel="noopener"><v-icon x-small>mdi-open-in-new</v-icon> Google Maps</a>
            <a :href="osmUrl" target="_blank" rel="noopener"><v-icon x-small>mdi-open-in-new</v-icon> OpenStreetMap</a>
          </div>

          <div class="amap__footer">
            <v-btn text @click="close">Abbrechen</v-btn>
            <v-spacer />
            <v-btn depressed color="success" :disabled="!changed" :loading="saving" @click="save">Speichern</v-btn>
          </div>
        </div>

        <!-- Rechts: Karte zur (bearbeiteten) Adresse, zieht beim Tippen verzögert nach -->
        <div class="amap__map">
          <iframe :key="mapQuery" :src="embedUrl" loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="Karte" />
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
// Versandliste (Malte 16.10.): Adresse prüfen UND bearbeiten in einem Fenster. Links die Felder des
// bisherigen Bearbeiten-Dialogs, rechts Google Maps zur aktuellen Eingabe. Speichern wie
// EditOrderAddressDialog (merge auf shippingAddress, Adress-Hold lösen) — der Geprüft-Haken wird dabei
// nicht überschrieben, er gehört der Tabelle bzw. dem Knopf hier.
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../../../fb";
import { EventBus, events } from "../../../../event-bus";
const _ = require("lodash");

const FIELDS = ["firstName", "lastName", "addressPrimary", "addressSecondary", "company", "postCode", "city", "province", "countryCode", "phone"];
const regionNames = typeof Intl !== "undefined" && Intl.DisplayNames ? new Intl.DisplayNames(["de"], { type: "region" }) : null;
const pick = (sa) => Object.fromEntries(FIELDS.map((f) => [f, (sa && sa[f]) || ""]));

export default {
  name: "AddressMapDialog",
  props: {
    show: { type: Boolean, default: false },
    order: { type: Object, default: null },
  },
  data: () => ({ form: null, original: null, mapQuery: "", saving: false }),
  computed: {
    liveChecked() {
      return !!(this.order && this.order.shippingAddress && this.order.shippingAddress.checked);
    },
    changed() {
      return !!this.form && !_.isEqual(this.form, this.original);
    },
    countryName() {
      const cc = String((this.form && this.form.countryCode) || "").trim().toUpperCase();
      try {
        return (regionNames && cc.length === 2 && regionNames.of(cc)) || cc;
      } catch (e) {
        return cc;
      }
    },
    query() {
      if (!this.form) return "";
      const f = this.form;
      return [f.addressPrimary, f.addressSecondary, [f.postCode, f.city].filter(Boolean).join(" "), this.countryName]
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .join(", ");
    },
    embedUrl() {
      return `https://www.google.com/maps?q=${encodeURIComponent(this.mapQuery)}&z=17&output=embed`;
    },
    googleUrl() {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.query)}`;
    },
    osmUrl() {
      return `https://www.openstreetmap.org/search?query=${encodeURIComponent(this.query)}`;
    },
    hints() {
      if (!this.form) return [];
      const f = this.form;
      const out = [];
      const street = `${f.addressPrimary} ${f.addressSecondary}`;
      if (!/\d/.test(street)) out.push("Keine Hausnummer erkannt");
      if (!String(f.postCode).trim()) out.push("Postleitzahl fehlt");
      if (!String(f.city).trim()) out.push("Ort fehlt");
      if (!/^[A-Za-z]{2}$/.test(String(f.countryCode).trim())) out.push("Ländercode muss zweistellig sein, z. B. DE");
      if (/packstation|postfiliale|paketshop/i.test(street) && !/\d{6,}/.test(`${street} ${f.company}`)) out.push("Packstation/Filiale ohne Postnummer?");
      return out;
    },
  },
  watch: {
    // Neue Bestellung geöffnet → Formular frisch aus der Bestellung (Live-Updates überschreiben laufende Eingaben nicht)
    "order.uid": {
      immediate: true,
      handler() {
        this.reset();
      },
    },
    show(v) {
      if (v) this.reset();
    },
    query(v) {
      this.debouncedMap(v);
    },
  },
  created() {
    this.debouncedMap = _.debounce((v) => (this.mapQuery = v), 700);
  },
  methods: {
    reset() {
      if (!this.order) return;
      this.form = pick(this.order.shippingAddress);
      this.original = _.cloneDeep(this.form);
      this.mapQuery = this.query;
    },
    close() {
      this.$emit("close");
    },
    async save() {
      if (!this.changed) return;
      this.saving = true;
      try {
        // wie EditOrderAddressDialog: Adressänderung löst den Adress-Hold; Support-Hold bleibt, wenn ein Quelp-Ticket hält
        const shippingAddress = { ...this.form, countryCode: String(this.form.countryCode || "").trim().toUpperCase() };
        const patch = { shippingAddress };
        if (this.order.resolveOnNewAddress) {
          patch.resolveOnNewAddress = false;
          const mirror = this.$store.getters.ticketByOrder ? this.$store.getters.ticketByOrder(this.order.uid) : null;
          patch.supportPending = !!(mirror && mirror.quelpOnHold);
        }
        await setDoc(doc(db, "orders", String(this.order.uid)), patch, { merge: true });
        this.original = _.cloneDeep(this.form);
        EventBus.$emit(events.SNACKBAR, { message: "Adresse gespeichert", type: "success" });
      } catch (e) {
        console.error("Adresse speichern:", e);
        EventBus.$emit(events.SNACKBAR, { message: `Speichern fehlgeschlagen: ${e.message || e}`, type: "error" });
      } finally {
        this.saving = false;
      }
    },
    copy() {
      const f = this.form || {};
      const text = [[f.firstName, f.lastName].filter(Boolean).join(" "), f.company, f.addressPrimary, f.addressSecondary, `${f.postCode} ${f.city}`.trim(), this.countryName].filter(Boolean).join("\n");
      if (navigator.clipboard) navigator.clipboard.writeText(text);
      EventBus.$emit(events.SNACKBAR, { message: "Adresse kopiert", type: "success" });
    },
  },
};
</script>

<style lang="scss" scoped>
.amap {
  border-radius: 16px !important;
  overflow: hidden;
  &__head { display: flex; align-items: center; padding: 14px 18px 10px; }
  &__title { font-weight: 700; font-size: 16px; }
  &__sub { font-size: 12px; color: rgba(0, 0, 0, 0.5); }
  &__body { display: grid; grid-template-columns: 420px 1fr; min-height: 560px; border-top: 1px solid rgba(0, 0, 0, 0.06); }
  &__form { padding: 16px 18px; display: flex; flex-direction: column; gap: 12px; border-right: 1px solid rgba(0, 0, 0, 0.06); }
  &__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  &__full { grid-column: 1 / -1; }
  &__hints { display: grid; gap: 4px; }
  &__hint { font-size: 12px; color: #b26a00; background: rgba(240, 169, 46, 0.12); border-radius: 8px; padding: 4px 8px; }
  &__row { display: flex; gap: 6px; flex-wrap: wrap; .v-btn { text-transform: none; letter-spacing: 0; } }
  &__links { display: flex; gap: 14px; font-size: 12px; a { text-decoration: none; } }
  &__footer { display: flex; align-items: center; margin-top: auto; padding-top: 8px; border-top: 1px solid rgba(0, 0, 0, 0.06); .v-btn { text-transform: none; letter-spacing: 0; } }
  &__map { background: #f2f2f0; iframe { width: 100%; height: 100%; min-height: 560px; border: 0; display: block; } }
}
@media (max-width: 900px) {
  .amap__body { grid-template-columns: 1fr; }
  .amap__form { border-right: 0; border-bottom: 1px solid rgba(0, 0, 0, 0.06); }
}
</style>
